import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CtaButton } from '@/components/Cta/Cta';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import i18n from '@/settings/i18n';
import Notice from './Notice';
import {
  BusinessNameForm as FormT,
  INPUT_CLASS,
  businessNameSchema,
} from './constants';

const t = i18n.init('business');

/** "One more thing": the legal business name of a signed-in customer. */
const BusinessNameForm = ({ initialValue }: { initialValue?: string }) => {
  const { customer, updateBusinessName } = useCustomerContext();
  const form = useForm<FormT>({
    resolver: yupResolver(businessNameSchema),
    defaultValues: {
      businessName: initialValue ?? customer?.businessName ?? '',
    },
  });
  const { errors } = form.formState;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async ({ businessName }: FormT) => {
    setSubmitting(true);
    setError('');

    const saved = await updateBusinessName(businessName);

    setSubmitting(false);

    if (!saved) {
      setError(t`We couldn't save your business name. Please try again.`);
    }
  };

  return (
    <form
      className="flex flex-col gap-3 text-sm"
      onSubmit={form.handleSubmit(submit)}
      noValidate
    >
      <div>
        <h2 className="font-medium">
          {customer?.firstName
            ? t`One more thing ${{ firstName: customer.firstName }}`
            : t`One more thing`}
        </h2>
        <p className="mt-1 text-xs text-gray-700/60">
          {t`We need your business name to link your tax exemption to your account.`}
        </p>
      </div>
      <Notice>
        {t`Important: your legal business name must be the same as on your exemption certificate.`}
      </Notice>
      <div>
        <label
          htmlFor="tax-exempt-business-name"
          className="mb-1 block text-xs font-medium"
        >
          {t`Legal business name`}
        </label>
        <input
          id="tax-exempt-business-name"
          className={INPUT_CLASS}
          placeholder={t`e.g. Acme Solutions Inc.`}
          aria-invalid={!!errors.businessName}
          autoComplete="organization"
          {...form.register('businessName')}
        />
        {errors.businessName && (
          <div className="mt-1 text-xs text-red">
            {errors.businessName.message}
          </div>
        )}
      </div>
      <CtaButton
        type="submit"
        className="!h-11 w-full !rounded !text-sm"
        loading={submitting}
      >
        {t`Save and continue`}
      </CtaButton>
      {error && <div className="text-xs text-red">{error}</div>}
    </form>
  );
};

export default BusinessNameForm;
